import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Palette, FileText } from 'lucide-react';

const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const Capabilities = () => {
  const capabilityBlocks = [
    {
      icon: Target,
      title: 'Revenue & Sales Enablement',
      intro: 'Strategic commercial frameworks that accelerate pipeline velocity, optimise deal cycles, and build sustainable revenue architecture.',
      services: [
        'Commercial strategy development',
        'Partnership and alliance formation',
        'Go-to-market execution',
        'Deal systems and process design',
        'Sales team enablement',
        'Revenue operations consulting'
      ],
      idealFor: [
        'Scaling B2B companies seeking structured growth',
        'Organisations entering new markets',
        'Teams optimising complex deal cycles'
      ],
      outcomes: [
        'Accelerated pipeline velocity',
        'Improved conversion rates',
        'Scalable revenue processes',
        'Strategic partnership portfolios'
      ],
      formats: [
        'Advisory retainers',
        'Project-based engagements',
        'Embedded team augmentation'
      ]
    },
    {
      icon: Palette,
      title: 'Creative & Brand Marketing',
      intro: 'Brand positioning and campaign orchestration that captures attention, builds equity, and converts sophisticated audiences.',
      services: [
        'Brand positioning and architecture',
        'Campaign strategy and creative direction',
        'Design direction and visual systems',
        'Product storytelling and launches',
        'Market positioning',
        'Creative team leadership'
      ],
      idealFor: [
        'Companies redefining their market position',
        'Brands launching premium products',
        'Organisations seeking creative differentiation'
      ],
      outcomes: [
        'Distinctive brand positioning',
        'Increased market recognition',
        'Cohesive visual identity',
        'Effective campaign performance'
      ],
      formats: [
        'Strategic brand projects',
        'Ongoing creative direction',
        'Campaign-specific engagements'
      ]
    },
    {
      icon: FileText,
      title: 'Content & Media',
      intro: 'Editorial systems and content infrastructure that establish thought leadership, drive organic growth, and build owned audiences.',
      services: [
        'Content strategy development',
        'Production system design',
        'Performance content optimisation',
        'Editorial calendar management',
        'Thought leadership programs',
        'Distribution strategy'
      ],
      idealFor: [
        'Companies building owned media presence',
        'Leaders establishing thought leadership',
        'Teams scaling content operations'
      ],
      outcomes: [
        'Established editorial authority',
        'Sustainable content systems',
        'Organic audience growth',
        'Measurable content ROI'
      ],
      formats: [
        'Content strategy sprints',
        'Ongoing production support',
        'Editorial leadership'
      ]
    }
  ];

  return (
    <main data-testid="capabilities-page" className="pt-36 md:pt-40">
      {/* Hero Section */}
      <section 
        data-testid="capabilities-hero"
        className="bg-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl"
            initial="hidden"
            animate="visible"
            variants={fadeUpVariant}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Our capabilities
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium text-charcoal tracking-tight leading-[1.05] mb-8">
              Integrated Growth Partnership
            </h1>
            <p className="text-lg md:text-xl text-softcharcoal/80 font-light leading-relaxed max-w-3xl">
              We operate as an integrated commercial and creative growth partner, combining strategic rigour with creative excellence. Our multi-disciplinary approach ensures alignment across all customer touchpoints.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Specialist Pods Note */}
      <section className="bg-ivory pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="bg-muted/50 border border-softcharcoal/5 p-8 md:p-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-start space-x-4">
              <span className="w-8 h-px bg-gold mt-3 flex-shrink-0"></span>
              <div>
                <p className="text-base md:text-lg text-softcharcoal font-light leading-relaxed">
                  <span className="font-medium text-charcoal">We assemble specialist pods per engagement.</span> Each team is carefully curated from our network of senior practitioners, ensuring you receive exactly the expertise your project requires—nothing more, nothing less.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capability Blocks */}
      {capabilityBlocks.map((capability, index) => (
        <section 
          key={capability.title}
          data-testid={`capability-block-${index + 1}`}
          className={`py-16 md:py-20 ${index % 2 === 1 ? 'bg-charcoal text-ivory' : 'bg-ivory'}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              {/* Left Column - Title & Intro */}
              <motion.div 
                className="lg:col-span-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUpVariant}
                transition={{ duration: 0.6 }}
              >
                <div className={`w-12 h-12 flex items-center justify-center border ${index % 2 === 1 ? 'border-ivory/20' : 'border-softcharcoal/10'} mb-6`}>
                  <capability.icon size={24} className="text-gold" strokeWidth={1.5} />
                </div>
                <h2 className={`font-heading text-3xl md:text-4xl font-medium tracking-tight mb-6 ${index % 2 === 1 ? 'text-ivory' : 'text-charcoal'}`}>
                  {capability.title}
                </h2>
                <p className={`text-base md:text-lg font-light leading-relaxed ${index % 2 === 1 ? 'text-ivory/70' : 'text-softcharcoal/70'}`}>
                  {capability.intro}
                </p>
                
                {/* Services List */}
                <div className="mt-10">
                  <h4 className={`text-xs uppercase tracking-[0.2em] font-semibold mb-4 ${index % 2 === 1 ? 'text-ivory/50' : 'text-softcharcoal/50'}`}>
                    Services
                  </h4>
                  <ul className="space-y-3">
                    {capability.services.map((service) => (
                      <li 
                        key={service} 
                        className={`text-sm font-light flex items-center ${index % 2 === 1 ? 'text-ivory/80' : 'text-softcharcoal/80'}`}
                      >
                        <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 flex-shrink-0"></span>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Right Column - Details */}
              <motion.div 
                className="lg:col-span-7"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUpVariant}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Ideal For */}
                  <div className={`p-8 border ${index % 2 === 1 ? 'border-ivory/10 bg-ivory/5' : 'border-softcharcoal/5 bg-white/50'}`}>
                    <h4 className={`text-xs uppercase tracking-[0.2em] font-semibold mb-6 ${index % 2 === 1 ? 'text-gold' : 'text-gold'}`}>
                      Ideal For
                    </h4>
                    <ul className="space-y-4">
                      {capability.idealFor.map((item) => (
                        <li 
                          key={item} 
                          className={`text-sm font-light leading-relaxed ${index % 2 === 1 ? 'text-ivory/70' : 'text-softcharcoal/70'}`}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Typical Outcomes */}
                  <div className={`p-8 border ${index % 2 === 1 ? 'border-ivory/10 bg-ivory/5' : 'border-softcharcoal/5 bg-white/50'}`}>
                    <h4 className={`text-xs uppercase tracking-[0.2em] font-semibold mb-6 ${index % 2 === 1 ? 'text-gold' : 'text-gold'}`}>
                      Typical Outcomes
                    </h4>
                    <ul className="space-y-4">
                      {capability.outcomes.map((item) => (
                        <li 
                          key={item} 
                          className={`text-sm font-light leading-relaxed ${index % 2 === 1 ? 'text-ivory/70' : 'text-softcharcoal/70'}`}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Engagement Formats - Full Width */}
                  <div className={`md:col-span-2 p-8 border ${index % 2 === 1 ? 'border-ivory/10 bg-ivory/5' : 'border-softcharcoal/5 bg-white/50'}`}>
                    <h4 className={`text-xs uppercase tracking-[0.2em] font-semibold mb-6 ${index % 2 === 1 ? 'text-gold' : 'text-gold'}`}>
                      Engagement Formats
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {capability.formats.map((format) => (
                        <span 
                          key={format}
                          className={`px-4 py-2 text-xs uppercase tracking-wider font-medium border ${
                            index % 2 === 1 
                              ? 'border-ivory/20 text-ivory/80' 
                              : 'border-softcharcoal/10 text-softcharcoal/80'
                          }`}
                        >
                          {format}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section 
        data-testid="capabilities-cta"
        className="bg-ivory py-16 md:py-20 border-t border-softcharcoal/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Next step
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-charcoal tracking-tight font-medium mb-6">
              Let's Discuss Your Needs
            </h2>
            <p className="text-lg text-softcharcoal/70 font-light leading-relaxed mb-10">
              Every engagement begins with a conversation. Share your objectives and we'll outline how we can help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="capabilities-cta-primary"
                className="bg-charcoal text-ivory px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-softcharcoal transition-colors duration-300 inline-flex items-center space-x-3"
              >
                <span>Request Introduction</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="#capabilities-deck"
                data-testid="capabilities-cta-secondary"
                className="border border-charcoal text-charcoal px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-charcoal hover:text-ivory transition-colors duration-300"
              >
                Download Deck
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Capabilities;
